import type { ScopedThreadRef } from "@amoreg/contracts";
import { create } from "zustand";

import { fnv1a32 } from "./lib/hash";
import type { PreviewMiniPlayerPosition, PreviewMiniPlayerSize } from "./previewMiniPlayerStore";

/**
 * A chat figure popped out of its message into a floating window over the
 * chat column. One window at a time, and it is not tied to a thread: a chart
 * stays up while the reader scrolls away from it or opens another thread to
 * compare.
 */
interface FloatingFigureBase {
  /** Identifies the figure, so floating the same one again closes it. */
  readonly id: string;
  readonly title: string | null;
  /** The figure's size in the message; the window keeps its aspect ratio. */
  readonly source: PreviewMiniPlayerSize;
}

/** A ```chart or ```mermaid fence, re-rendered from its source. */
export interface FloatingFenceFigure extends FloatingFigureBase {
  readonly kind: "fence";
  readonly language: "chart" | "mermaid";
  readonly code: string;
}

/**
 * An interactive HTML page from a thread's workspace. It names its thread,
 * so it keeps showing that thread's page while the reader opens another.
 */
export interface FloatingPageFigure extends FloatingFigureBase {
  readonly kind: "page";
  readonly threadRef: ScopedThreadRef;
  readonly path: string;
}

export type FloatingFigure = FloatingFenceFigure | FloatingPageFigure;

interface FloatingFigureState {
  readonly figure: FloatingFigure | null;
  readonly position: PreviewMiniPlayerPosition | null;
  readonly width: number | null;
  readonly maximized: boolean;
  /** Floats a figure, or closes the window when that figure is already up. */
  readonly toggle: (figure: FloatingFigure, width: number) => void;
  readonly close: () => void;
  readonly move: (position: PreviewMiniPlayerPosition | null) => void;
  readonly resize: (width: number | null) => void;
  readonly setMaximized: (maximized: boolean) => void;
}

export const useFloatingFigureStore = create<FloatingFigureState>()((set) => ({
  figure: null,
  position: null,
  width: null,
  maximized: false,
  toggle: (figure, width) =>
    set((state) =>
      state.figure?.id === figure.id
        ? { figure: null, maximized: false }
        : // A new figure starts in the top-right corner at the given width;
          // the previous window's place belongs to a different shape.
          { figure, width, position: null, maximized: false },
    ),
  close: () => set({ figure: null, maximized: false }),
  move: (position) =>
    set((state) =>
      state.position?.x === position?.x && state.position?.y === position?.y ? state : { position },
    ),
  resize: (width) => set((state) => (state.width === width ? state : { width })),
  setMaximized: (maximized) => set({ maximized }),
}));

/** A stable id for a fence body: same language and code, same figure. */
export function floatingFigureId(language: string, code: string): string {
  return `${language}:${fnv1a32(code).toString(36)}:${code.length}`;
}
